import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import type { UserProfile } from 'src/types/social';
import { useAccount } from 'wagmi';
import { SignatureContext } from './SignatureContext';

interface ProfileContextType {
  profile: UserProfile | null;
  isProfileLoading: boolean;
  linkedWallets: string[];
  refreshProfile: () => Promise<void>;
  isProfileComplete: boolean;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { address, isConnected } = useAccount();
  const { signature } = useContext(SignatureContext);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [linkedWallets, setLinkedWallets] = useState<string[]>([]);

  const isProfileComplete = (profile: UserProfile | null): boolean => {
    if (!profile) { return false; }
    return !!(
      profile.avatar_url &&
      profile.bio &&
      profile.telegram_handle &&
      profile.twitter_handle &&
      profile.bio.trim() !== '' &&
      profile.telegram_handle.trim() !== '' &&
      profile.twitter_handle.trim() !== ''
    );
  };

  const fetchProfile = useCallback(async () => {
    if (!isConnected || !signature || !address) {
      setProfile(null);
      return;
    }

    setIsProfileLoading(true);
    try {
      const response = await fetch(`/api/profile?address=${address}`, {
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        // Dispatch event for other components
        window.dispatchEvent(new Event('profileUpdated'));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setIsProfileLoading(false);
    }
  }, [isConnected, signature, address]);

  const fetchLinkedWallets = useCallback(async () => {
    if (!signature || !address) { return; }

    try {
      const response = await fetch('/api/profile/linked-wallets', {
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLinkedWallets(data.linked_wallets || []);
      }
    } catch (error) {
      console.error('Error fetching linked wallets:', error);
    }
  }, [signature, address]);

  useEffect(() => {
    fetchProfile();
    fetchLinkedWallets();
  }, [fetchProfile, fetchLinkedWallets]);

  const value = {
    profile,
    isProfileLoading,
    linkedWallets,
    refreshProfile: fetchProfile,
    isProfileComplete: isProfileComplete(profile),
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
